/**
 * Our Styles in the Wild
 *
 * Yotpo's product gallery only shows itself once a product has 3 or more
 * customer posts. This element asks Yotpo for the product's posts on every
 * page load and picks a layout from the count Yotpo reports:
 *
 *   0 posts       stays hidden
 *   1 post        data-layout="single"   photo beside the caption
 *   2 posts       data-layout="double"   two photos side by side
 *   3+ posts      data-layout="carousel" tag line only, revealed once Yotpo's
 *                                        own carousel has rendered above it
 *
 * The feed is the one Yotpo's gallery is built from (the product album), so
 * the same posts appear, including Instagram posts whose rights request Yotpo
 * marks as rejected. Until the count is known, and whenever the feed is slow
 * or fails, nothing changes on the page.
 *
 * Theme editor settings arrive as data attributes:
 *   data-tag-line="false"        tag line off, so 3+ posts show nothing here
 *   data-instagram-only="true"   a lone non-Instagram post shows no section
 *   data-badges="true"           "Instagram" / "Review" badge on each tile
 *   data-shape="original"        photos keep their own proportions; the
 *                                section waits for them so nothing shifts
 *
 * Clicking a photo in the 1 and 2 post layouts opens the pop-up (UgcPopup
 * below); its arrows, and a swipe on a phone, move between the product's
 * posts.
 *
 * Video posts show a still frame and a play icon. Hovering one with a mouse
 * plays the clip muted in the tile, and moving away stops it; a tap on a
 * phone opens the pop-up, where the clip plays with its own controls. With
 * reduce motion on, nothing plays on hover. No video is downloaded until a
 * shopper hovers over or opens one: the <video> element is only created then.
 */
(function () {
  'use strict';

  const TAG_NAME = 'ugc-styles-in-the-wild';
  if (!window.customElements || window.customElements.get(TAG_NAME)) return;

  const ALBUM_ENDPOINT = 'https://api-cdn.yotpo.com/v1/widget/';
  const FEED_TIMEOUT_MS = 6000;
  const CAROUSEL_MIN_POSTS = 3;
  const HOST_EMPTY_CLASS = 'ugc-wild-host--empty';
  const TILE_SIZE = 640;
  const ORIGINAL_SHAPE_TIMEOUT_MS = 4000;
  const SWIPE_MIN_PX = 40;
  const SCROLL_LOCK_CLASS = 'overflow-hidden';
  const POPUP_FRAME_SIZE = 1080;
  const VIDEO_CONTROLS_PX = 64;
  const PLAYING_CLASS = 'is-playing';
  const SVG_NS = 'http://www.w3.org/2000/svg';

  /**
   * Analytics. Yotpo's dashboards do not count activity in this section, so
   * it reports three events of its own:
   *
   *   ugc_section_view    once per page view, when the revealed section is
   *                       at least half on screen
   *   ugc_photo_open      a photo or video tile opens the pop-up
   *   ugc_shop_now_click  Shop now in the pop-up is clicked
   *
   * Each goes to Shopify's customer events (Shopify.analytics.publish), where
   * the store's pixels subscribe to it and Shopify applies the shopper's
   * consent choices. When a Google Tag Manager container is on the page, the
   * same event and fields are also pushed to its data layer.
   */
  const VIEW_EVENT = 'ugc_section_view';
  const OPEN_EVENT = 'ugc_photo_open';
  const SHOP_EVENT = 'ugc_shop_now_click';
  const VIEW_RATIO = 0.5;
  const LAYOUT_NAMES = {
    single: 'spotlight',
    double: 'side_by_side',
    carousel: 'tag_line_only'
  };
  const viewedSections = {};

  function hasTagManager() {
    const containers = window.google_tag_manager;
    if (!containers || typeof containers !== 'object') return false;
    return Object.keys(containers).some(function (key) {
      return key.indexOf('GTM-') === 0;
    });
  }

  /*
   * A failing pixel must never break the section, nor stop the event
   * reaching Tag Manager.
   */
  function publishCustomerEvent(name, data) {
    const analytics = window.Shopify && window.Shopify.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return false;
    try {
      analytics.publish(name, data);
      return true;
    } catch {
      return false;
    }
  }

  function track(name, data) {
    publishCustomerEvent(name, data);
    if (hasTagManager()) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, data));
    }
  }

  function prefersReducedMotion() {
    return Boolean(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /**
   * Yotpo's still frame for a video is Kaltura's default thumbnail, a tiny
   * 51 x 90 image. Kaltura renders the same frame at any width when the path
   * ends in /width/<px>, so the tile and the pop-up ask for a sharp one.
   */
  function frameAt(url, width) {
    if (!url) return url;
    try {
      const parsed = new window.URL(url);
      if (
        !/(^|\.)kaltura\.com$/.test(parsed.hostname) ||
        parsed.pathname.indexOf('/thumbnail/') === -1 ||
        /\/width\/\d+/.test(parsed.pathname)
      ) {
        return url;
      }
      parsed.pathname = parsed.pathname.replace(/\/+$/, '') + '/width/' + width;
      return parsed.href;
    } catch {
      return url;
    }
  }

  function playIcon() {
    const icon = document.createElement('span');
    icon.className = 'ugc-wild__play';
    icon.setAttribute('aria-hidden', 'true');
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'M8 5.5v13l10.5-6.5z');
    path.setAttribute('fill', 'currentColor');
    svg.appendChild(path);
    icon.appendChild(svg);
    return icon;
  }

  function pauseVideo(video) {
    if (video && !video.paused) video.pause();
  }

  function toHttps(url) {
    if (typeof url !== 'string' || !url) return null;
    const absolute = url.indexOf('//') === 0 ? 'https:' + url : url;
    try {
      const parsed = new window.URL(absolute);
      return parsed.protocol === 'https:' ? parsed.href : null;
    } catch {
      return null;
    }
  }

  function toPost(item) {
    if (!item) return null;
    const mediaType = item.media_type === 'video' ? 'video' : 'image';
    const candidates =
      mediaType === 'video'
        ? [item.thumbnail_url, item.medium_image_url, item.image_url]
        : [item.medium_image_url, item.original_image_url, item.image_url];
    let src = null;
    for (let i = 0; i < candidates.length && !src; i++) {
      src = toHttps(candidates[i]);
    }
    if (!src) return null;
    const video = mediaType === 'video' ? toHttps(item.standard_resolution_url) : null;
    const source =
      item.source === 'instagram' || item.source === 'review' ? item.source : 'other';
    const review = item.review && typeof item.review === 'object' ? item.review : null;
    const instagram = item.post && typeof item.post === 'object' ? item.post : null;
    return {
      id: String(item.image_id || ''),
      source: source,
      mediaType: mediaType,
      src: mediaType === 'video' ? frameAt(src, TILE_SIZE) : src,
      fallback: mediaType === 'video' ? src : null,
      video: video,
      large:
        mediaType === 'image'
          ? toHttps(item.original_image_url) || toHttps(item.medium_image_url) || src
          : frameAt(src, POPUP_FRAME_SIZE),
      author: toAuthor(source, instagram, review),
      verifiedBuyer: Boolean(review && review.verified_buyer === true),
      caption: toText(source === 'review' ? review && review.content : instagram && instagram.content),
      products: toTaggedProducts(item.tagged_products)
    };
  }

  function toText(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function toAuthor(source, instagram, review) {
    if (source === 'instagram') {
      const handle = toText(instagram && instagram.username).replace(/^@+/, '');
      return handle ? '@' + handle : '';
    }
    if (source === 'review') {
      return toText(review && review.user && review.user.display_name);
    }
    return '';
  }

  /**
   * Tagged products arrive with a full product URL. Only the handle is kept,
   * and the link is rebuilt on this storefront, so a post can never send a
   * shopper to another site.
   */
  function toProductHandle(url) {
    if (typeof url !== 'string' || !url) return null;
    try {
      const parsed = new window.URL(url, window.location.origin);
      const match = parsed.pathname.match(/\/products\/([^/]+)\/?$/);
      if (!match) return null;
      const handle = decodeURIComponent(match[1]).trim();
      return handle && handle.indexOf('/') === -1 ? handle : null;
    } catch {
      return null;
    }
  }

  function toTaggedProducts(list) {
    if (!Array.isArray(list)) return [];
    const products = [];
    const seen = {};
    for (let i = 0; i < list.length; i++) {
      const entry = list[i];
      if (!entry) continue;
      const handle = toProductHandle(entry.product_url) || toProductHandle(entry.product_link);
      const name = toText(entry.name);
      if (!handle || !name || seen[handle]) continue;
      seen[handle] = true;
      products.push({ handle: handle, name: name, image: toHttps(entry.image_url) });
    }
    return products;
  }

  function hasMedia(widget) {
    return Boolean(widget && widget.querySelector('img, video, .yotpo-image'));
  }

  function focusableIn(root) {
    const nodes = root.querySelectorAll(
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), [tabindex="0"], video[controls]'
    );
    return Array.prototype.filter.call(nodes, function (node) {
      return node.getClientRects().length > 0 && !node.closest('[hidden]');
    });
  }

  /**
   * The pop-up a photo opens: the photo large on one side, the post's tagged
   * products, credit and caption on the other, laid out like Yotpo's own
   * gallery pop-up. Markup and copy live in the section; this fills it in.
   *
   * The media slot is filled by renderMedia(), so a video can take the
   * photo's place there without touching the rest of the pop-up.
   */
  class UgcPopup {
    constructor(host) {
      this.host = host;
      this.dialog = host.querySelector('[data-ugc-popup]');
      this.posts = [];
      this.index = 0;
      this.opener = null;
      if (!this.dialog || typeof this.dialog.showModal !== 'function') {
        this.dialog = null;
        return;
      }
      this.find = (name) => this.dialog.querySelector('[data-ugc-popup-' + name + ']');
      this.onClick = this.onClick.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.dialog.addEventListener('click', this.onClick);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('keydown', this.onKeydown);
      this.dialog.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.dialog.addEventListener('touchend', this.onTouchEnd, { passive: true });
    }

    destroy() {
      if (!this.dialog) return;
      if (this.dialog.open) this.dialog.close();
      this.unlockScroll();
      this.dialog.removeEventListener('click', this.onClick);
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('close', this.onClose);
      this.dialog.removeEventListener('keydown', this.onKeydown);
      this.dialog.removeEventListener('touchstart', this.onTouchStart);
      this.dialog.removeEventListener('touchend', this.onTouchEnd);
      this.dialog = null;
    }

    open(posts, index, opener) {
      if (!this.dialog || !posts.length) return;
      this.posts = posts.slice();
      this.opener = opener || null;
      this.dialog.dataset.count = String(this.posts.length);
      this.render(index);
      if (!this.dialog.open) {
        this.lockScroll();
        this.dialog.showModal();
      }
      this.measureCaption();
      const close = this.find('close');
      if (close) close.focus();
    }

    close() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    currentVideo() {
      const media = this.find('media');
      return media ? media.querySelector('video') : null;
    }

    step(delta) {
      const count = this.posts.length;
      if (count < 2) return;
      this.render((this.index + delta + count) % count);
      this.measureCaption();
    }

    render(index) {
      const post = this.posts[index];
      if (!post) return;
      this.index = index;
      this.dialog.dataset.source = post.source;
      this.dialog.dataset.mediaType = post.mediaType;

      const media = this.find('media');
      if (media) {
        pauseVideo(this.currentVideo());
        media.replaceChildren(this.renderMedia(post));
      }

      this.renderProducts(post.products);
      this.renderCredit(post);
      this.renderCaption(post.caption);

      const content = this.find('details');
      if (content) content.scrollTop = 0;
      const panel = content && content.parentNode;
      if (panel) panel.scrollTop = 0;
    }

    renderMedia(post) {
      if (post.mediaType === 'video' && post.video) return this.renderVideo(post);
      const image = document.createElement('img');
      image.className = 'ugc-pop__image';
      image.decoding = 'async';
      image.alt = this.host.altFor(post);
      if (post.large !== post.src) {
        image.addEventListener(
          'error',
          function () {
            image.src = post.src;
          },
          { once: true }
        );
      }
      image.src = post.large;
      return image;
    }

    /**
     * The clip with the browser's own play/pause, sound and progress
     * controls. It starts playing with sound, since opening the pop-up is the
     * shopper's own tap or click; a browser that refuses starts it muted, and
     * the sound control turns it up. With reduce motion on it waits for the
     * shopper to press play.
     */
    renderVideo(post) {
      const video = document.createElement('video');
      video.className = 'ugc-pop__image ugc-pop__video';
      video.controls = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.preload = 'metadata';
      video.setAttribute('aria-label', this.host.altFor(post));
      if (post.large) video.poster = post.large;
      video.src = post.video;
      if (!prefersReducedMotion()) {
        const started = video.play();
        if (started && typeof started.catch === 'function') {
          started.catch(function (error) {
            if (!error || error.name !== 'NotAllowedError' || !video.isConnected) return;
            video.muted = true;
            video.play().catch(function () {});
          });
        }
      }
      return video;
    }

    productUrl(handle) {
      const root = (this.host.dataset.productsRoot || '/').replace(/\/+$/, '');
      return root + '/products/' + encodeURIComponent(handle);
    }

    renderProducts(products) {
      const block = this.find('product');
      const first = products[0];
      if (block) block.hidden = !first;
      if (first) {
        const url = this.productUrl(first.handle);
        const imageLink = this.find('product-image');
        if (imageLink) {
          imageLink.href = url;
          imageLink.replaceChildren();
          if (first.image) {
            const image = document.createElement('img');
            image.loading = 'lazy';
            image.alt = '';
            image.width = 150;
            image.height = 150;
            image.src = first.image;
            imageLink.appendChild(image);
          }
        }
        const name = this.find('product-name');
        if (name) name.textContent = first.name;
        const shop = this.find('shop');
        if (shop) {
          shop.href = url;
          shop.dataset.productHandle = first.handle;
        }
      }

      const tagged = this.find('tagged');
      const list = this.find('tagged-list');
      const others = products.slice(1);
      if (tagged) tagged.hidden = others.length === 0;
      if (!list) return;
      list.replaceChildren();
      for (let i = 0; i < others.length; i++) {
        list.appendChild(this.renderTaggedProduct(others[i]));
      }
    }

    renderTaggedProduct(product) {
      const item = document.createElement('li');
      item.className = 'ugc-pop__tagged-item';
      const link = document.createElement('a');
      link.className = 'ugc-pop__tagged-link';
      link.href = this.productUrl(product.handle);
      link.dataset.productHandle = product.handle;
      if (product.image) {
        const image = document.createElement('img');
        image.loading = 'lazy';
        image.alt = product.name;
        image.width = 74;
        image.height = 74;
        image.src = product.image;
        link.appendChild(image);
      } else {
        link.setAttribute('aria-label', product.name);
      }
      link.title = product.name;
      item.appendChild(link);
      return item;
    }

    renderCredit(post) {
      const credit = this.find('credit');
      const name = this.find('name');
      const via = this.find('via');
      const avatar = this.find('avatar');
      let viaText = '';
      if (post.source === 'instagram') viaText = this.dialog.dataset.viaInstagram || '';
      if (post.source === 'review' && post.verifiedBuyer) {
        viaText = this.dialog.dataset.verifiedBuyer || '';
      }
      if (name) name.textContent = post.author;
      if (via) via.textContent = viaText;
      if (avatar) avatar.textContent = post.author.replace(/^@/, '').charAt(0).toUpperCase();
      if (credit) {
        credit.hidden = !post.author && !viaText;
        credit.dataset.hasAuthor = post.author ? 'true' : 'false';
      }
    }

    renderCaption(text) {
      const caption = this.find('caption');
      const more = this.find('more');
      if (caption) {
        caption.textContent = text;
        caption.hidden = !text;
        caption.classList.add('is-clamped');
      }
      if (more) {
        more.hidden = true;
        more.setAttribute('aria-expanded', 'false');
      }
    }

    measureCaption() {
      const caption = this.find('caption');
      const more = this.find('more');
      if (!caption || !more || caption.hidden) return;
      more.hidden = caption.scrollHeight <= caption.clientHeight + 1;
      if (more.hidden) caption.classList.remove('is-clamped');
    }

    expandCaption() {
      const caption = this.find('caption');
      const more = this.find('more');
      if (!caption || !more) return;
      caption.classList.remove('is-clamped');
      more.setAttribute('aria-expanded', 'true');
      more.hidden = true;
      caption.focus();
    }

    onClick(event) {
      if (event.target === this.dialog) {
        this.close();
        return;
      }
      const control = event.target.closest(
        '[data-ugc-popup-close], [data-ugc-popup-prev], [data-ugc-popup-next], [data-ugc-popup-more]'
      );
      const shop = event.target.closest('[data-ugc-popup-shop]');
      if (shop) this.trackShopNow(shop);
      if (!control) return;
      if (control.hasAttribute('data-ugc-popup-close')) this.close();
      else if (control.hasAttribute('data-ugc-popup-prev')) this.step(-1);
      else if (control.hasAttribute('data-ugc-popup-next')) this.step(1);
      else this.expandCaption();
    }

    trackShopNow(link) {
      const post = this.posts[this.index];
      const product = post && post.products[0];
      if (!product || link.dataset.productHandle !== product.handle) return;
      track(
        SHOP_EVENT,
        Object.assign(this.host.analyticsProduct(), {
          target_product_handle: product.handle,
          target_product_title: product.name,
          target_url: link.href,
          position: this.index + 1,
          source: post.source
        })
      );
    }

    onCancel(event) {
      event.preventDefault();
      this.close();
    }

    onClose() {
      pauseVideo(this.currentVideo());
      this.unlockScroll();
      const opener = this.opener;
      this.opener = null;
      if (opener && opener.isConnected) {
        opener.focus();
      } else {
        const fallback = this.host.querySelector('[data-ugc-open]');
        if (fallback) fallback.focus();
      }
    }

    onKeydown(event) {
      if (event.key !== 'Tab') return;
      const nodes = focusableIn(this.dialog);
      if (!nodes.length) {
        event.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      const inside = nodes.indexOf(active) !== -1;
      if (event.shiftKey && (active === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    }

    /**
     * A drag along the bottom of a video is the shopper seeking with its
     * progress bar, not a swipe to the next post.
     */
    onTouchStart(event) {
      if (event.touches.length !== 1) {
        this.touch = null;
        return;
      }
      const point = event.touches[0];
      const video = event.target.closest && event.target.closest('video');
      if (video && point.clientY > video.getBoundingClientRect().bottom - VIDEO_CONTROLS_PX) {
        this.touch = null;
        return;
      }
      this.touch = { x: point.clientX, y: point.clientY };
    }

    onTouchEnd(event) {
      const start = this.touch;
      this.touch = null;
      if (!start || !event.changedTouches.length || this.posts.length < 2) return;
      const dx = event.changedTouches[0].clientX - start.x;
      const dy = event.changedTouches[0].clientY - start.y;
      if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      this.step(dx < 0 ? 1 : -1);
    }

    lockScroll() {
      if (this.lockedScroll) return;
      this.lockedScroll = !document.body.classList.contains(SCROLL_LOCK_CLASS);
      if (this.lockedScroll) document.body.classList.add(SCROLL_LOCK_CLASS);
    }

    unlockScroll() {
      if (this.lockedScroll) document.body.classList.remove(SCROLL_LOCK_CLASS);
      this.lockedScroll = false;
    }
  }

  class UgcStylesInTheWild extends window.HTMLElement {
    connectedCallback() {
      this.controller = new window.AbortController();
      this.tilePosts = new window.WeakMap();
      this.popup = new UgcPopup(this);
      this.onTileClick = this.onTileClick.bind(this);
      this.onTileEnter = this.onTileEnter.bind(this);
      this.onTileLeave = this.onTileLeave.bind(this);
      this.addEventListener('click', this.onTileClick);
      this.load();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.carouselObserver) this.carouselObserver.disconnect();
      if (this.viewObserver) this.viewObserver.disconnect();
      this.viewObserver = null;
      this.stopTiles();
      this.removeEventListener('click', this.onTileClick);
      if (this.popup) this.popup.destroy();
      this.popup = null;
      this.releaseHost();
    }

    onTileClick(event) {
      const opener = event.target.closest('[data-ugc-open]');
      if (!opener || !this.contains(opener) || !this.popup) return;
      const tiles = this.currentTiles();
      const posts = tiles.map((tile) => this.tilePosts.get(tile));
      const index = tiles.indexOf(opener.closest('.ugc-wild__tile'));
      if (index < 0) return;
      event.preventDefault();
      this.stopTiles();
      this.popup.open(posts, index, opener);
      const post = posts[index];
      if (post && this.popup.dialog && this.popup.dialog.open) {
        track(
          OPEN_EVENT,
          Object.assign(this.analyticsProduct(), {
            layout: LAYOUT_NAMES[this.dataset.layout] || '',
            position: index + 1,
            source: post.source,
            media_type: post.mediaType,
            post_id: post.id
          })
        );
      }
    }

    /**
     * The product page the section sits on, as every event names it.
     */
    analyticsProduct() {
      return {
        product_id: this.dataset.productId || '',
        product_handle: this.dataset.analyticsProductHandle || '',
        product_title: this.dataset.analyticsProductTitle || ''
      };
    }

    /**
     * Fires ugc_section_view the first time the revealed section is at least
     * half on screen (or fills half the screen, for a section taller than
     * it). Once per section per page view, whatever the shopper does next.
     */
    watchView(postCount) {
      const key = this.dataset.sectionId || this.dataset.productId || '';
      if (viewedSections[key] || this.viewObserver) return;
      if (typeof window.IntersectionObserver !== 'function') return;
      this.viewObserver = new window.IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          if (!entry || !entry.isIntersecting || this.hidden) return;
          const viewport = window.innerHeight || document.documentElement.clientHeight;
          if (
            entry.intersectionRatio < VIEW_RATIO &&
            entry.intersectionRect.height < viewport * VIEW_RATIO
          ) {
            return;
          }
          this.viewObserver.disconnect();
          this.viewObserver = null;
          if (viewedSections[key]) return;
          viewedSections[key] = true;
          track(
            VIEW_EVENT,
            Object.assign(this.analyticsProduct(), {
              layout: LAYOUT_NAMES[this.dataset.layout] || '',
              post_count: postCount
            })
          );
        },
        { threshold: [0, 0.25, VIEW_RATIO, 0.75, 1] }
      );
      this.viewObserver.observe(this);
    }

    /**
     * Hover preview for video tiles: a mouse (or pen) pointer only, so a tap
     * on a phone goes straight to the pop-up. The <video> is created on the
     * first hover, which is when the clip starts downloading.
     */
    onTileEnter(event) {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      if (prefersReducedMotion()) return;
      const tile = event.currentTarget.closest('.ugc-wild__tile');
      const post = tile && this.tilePosts.get(tile);
      if (!post || !post.video) return;
      if (this.popup && this.popup.dialog && this.popup.dialog.open) return;

      let video = tile.querySelector('.ugc-wild__video');
      if (!video) {
        video = document.createElement('video');
        video.className = 'ugc-wild__video';
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.loop = true;
        video.preload = 'auto';
        video.disablePictureInPicture = true;
        video.setAttribute('aria-hidden', 'true');
        video.tabIndex = -1;
        video.addEventListener('playing', function () {
          if (tile.dataset.hover === 'true') tile.classList.add(PLAYING_CLASS);
        });
        video.src = post.video;
        event.currentTarget.appendChild(video);
      }
      tile.dataset.hover = 'true';
      const started = video.play();
      if (started && typeof started.catch === 'function') started.catch(function () {});
    }

    onTileLeave(event) {
      const tile = event.currentTarget.closest('.ugc-wild__tile');
      if (tile) this.stopTile(tile);
    }

    stopTile(tile) {
      delete tile.dataset.hover;
      tile.classList.remove(PLAYING_CLASS);
      const video = tile.querySelector('.ugc-wild__video');
      if (!video) return;
      pauseVideo(video);
      if (video.readyState > 0) video.currentTime = 0;
    }

    stopTiles() {
      const tiles = this.querySelectorAll('.ugc-wild__tile[data-media-type="video"]');
      for (let i = 0; i < tiles.length; i++) this.stopTile(tiles[i]);
    }

    currentTiles() {
      const list = this.querySelector('[data-ugc-tiles]');
      if (!list) return [];
      return Array.prototype.filter.call(list.children, (tile) => this.tilePosts.has(tile));
    }

    findHostWidget() {
      const productId = this.dataset.productId;
      const widgets = document.querySelectorAll('.yotpo-pictures-widget[data-product-id]');
      for (let i = 0; i < widgets.length; i++) {
        if (widgets[i].getAttribute('data-product-id') === productId) return widgets[i];
      }
      return null;
    }

    async load() {
      const appKey = window.yotpoAppKey;
      const productId = this.dataset.productId;
      if (!appKey || !productId) return;

      const controller = this.controller;
      const timer = setTimeout(function () {
        controller.abort();
      }, FEED_TIMEOUT_MS);

      let album;
      try {
        const url =
          ALBUM_ENDPOINT +
          encodeURIComponent(appKey) +
          '/albums/product/' +
          encodeURIComponent(productId) +
          '?per_page=' +
          CAROUSEL_MIN_POSTS;
        const response = await window.fetch(url, {
          signal: controller.signal,
          credentials: 'omit'
        });
        if (!response.ok) return;
        album = await response.json();
      } catch {
        return;
      } finally {
        clearTimeout(timer);
      }

      if (!this.isConnected || controller.signal.aborted) return;

      const body = album && album.response;
      const total = Number(body && body.pagination && body.pagination.total) || 0;
      if (total < 1) return;

      const host = this.findHostWidget();
      if (total >= CAROUSEL_MIN_POSTS || hasMedia(host)) {
        this.showUnderCarousel(host, total);
        return;
      }

      const items = Array.isArray(body.images) ? body.images : [];
      const posts = items.map(toPost).filter(Boolean).slice(0, total);
      if (!posts.length) return;
      if (
        this.dataset.instagramOnly === 'true' &&
        posts.length === 1 &&
        posts[0].source !== 'instagram'
      ) {
        return;
      }
      this.showPosts(posts, host);
    }

    showUnderCarousel(host, total) {
      if (!host || this.dataset.tagLine === 'false') return;
      const reveal = () => {
        if (this.carouselObserver) this.carouselObserver.disconnect();
        this.carouselObserver = null;
        this.dataset.layout = 'carousel';
        this.hidden = false;
        this.watchView(total);
      };
      if (hasMedia(host)) {
        reveal();
        return;
      }
      this.carouselObserver = new window.MutationObserver(function () {
        if (hasMedia(host)) reveal();
      });
      this.carouselObserver.observe(host, { childList: true, subtree: true });
    }

    async showPosts(posts, host) {
      const list = this.querySelector('[data-ugc-tiles]');
      if (!list) return;
      list.textContent = '';
      for (let i = 0; i < posts.length; i++) {
        list.appendChild(this.renderTile(posts[i]));
      }
      if (this.dataset.shape === 'original') {
        await this.sizeToOriginal(list);
        if (!this.isConnected || !list.children.length) return;
      }
      this.posts = posts;
      this.dataset.layout = list.children.length === 1 ? 'single' : 'double';
      this.hidden = false;
      this.collapseHost(host);
      this.watchView(list.children.length);
    }

    renderTile(post) {
      const tile = document.createElement('li');
      tile.className = 'ugc-wild__tile';
      tile.dataset.mediaId = post.id;
      tile.dataset.source = post.source;
      tile.dataset.mediaType = post.mediaType;

      const opener = document.createElement('button');
      opener.type = 'button';
      opener.className = 'ugc-wild__open';
      opener.setAttribute('data-ugc-open', '');
      opener.setAttribute('aria-haspopup', 'dialog');

      const image = document.createElement('img');
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = TILE_SIZE;
      image.height = TILE_SIZE;
      image.alt = this.altFor(post);
      image.addEventListener('error', () => {
        if (post.fallback && image.getAttribute('src') !== post.fallback) {
          image.src = post.fallback;
          return;
        }
        this.dropTile(tile);
      });
      image.src = post.src;

      opener.appendChild(image);
      if (post.mediaType === 'video' && post.video) {
        opener.appendChild(playIcon());
        opener.addEventListener('pointerenter', this.onTileEnter);
        opener.addEventListener('pointerleave', this.onTileLeave);
      }
      tile.appendChild(opener);
      this.tilePosts.set(tile, post);

      const badge = this.badgeFor(post);
      if (badge) {
        const label = document.createElement('span');
        label.className = 'ugc-wild__badge';
        label.setAttribute('aria-hidden', 'true');
        label.textContent = badge;
        tile.appendChild(label);
      }
      return tile;
    }

    badgeFor(post) {
      if (this.dataset.badges !== 'true') return '';
      if (post.source === 'instagram') return this.dataset.badgeInstagram || '';
      if (post.source === 'review') return this.dataset.badgeReview || '';
      return '';
    }

    sizeToOriginal(list) {
      const images = Array.prototype.slice.call(list.querySelectorAll('img'));
      const ready = images.map(function (image) {
        image.loading = 'eager';
        const decoded = typeof image.decode === 'function' ? image.decode() : Promise.reject();
        return decoded.then(
          function () {
            if (image.naturalWidth && image.naturalHeight) {
              image.width = image.naturalWidth;
              image.height = image.naturalHeight;
            }
          },
          function () {}
        );
      });
      const timeout = new Promise(function (resolve) {
        setTimeout(resolve, ORIGINAL_SHAPE_TIMEOUT_MS);
      });
      return Promise.race([Promise.all(ready), timeout]);
    }

    altFor(post) {
      const kind = post.mediaType === 'video' ? 'Video' : 'Photo';
      const bySource =
        post.source === 'instagram' ? 'Instagram' : post.source === 'review' ? 'Review' : '';
      return this.dataset['alt' + kind + bySource] || this.dataset['alt' + kind] || '';
    }

    dropTile(tile) {
      const list = tile.parentNode;
      if (!list) return;
      list.removeChild(tile);
      const remaining = list.children.length;
      if (remaining === 0) {
        this.hidden = true;
        delete this.dataset.layout;
        this.releaseHost();
      } else {
        this.dataset.layout = remaining === 1 ? 'single' : 'double';
      }
    }

    collapseHost(host) {
      if (!host || hasMedia(host)) return;
      const section = host.closest('.shopify-section');
      if (!section || section.contains(this)) return;
      section.classList.add(HOST_EMPTY_CLASS);
      this.hostSection = section;
    }

    releaseHost() {
      if (this.hostSection) this.hostSection.classList.remove(HOST_EMPTY_CLASS);
      this.hostSection = null;
    }
  }

  window.customElements.define(TAG_NAME, UgcStylesInTheWild);
})();
